import React, { useState, useEffect, useCallback } from 'react';
import { calendarApi } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  CalendarDays, Link2, Unlink, Plus, Clock, MapPin, Users,
  ExternalLink, Trash2, RefreshCw, Check, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const EAGLE_MASCOT = 'https://customer-assets.emergentagent.com/job_fa5ebc01-0c7f-4b55-8e38-1fa2a64145ae/artifacts/5x8b7wxy_Transparent%20BG%20Eagle.png';

const GoogleCalendar = () => {
  const [status, setStatus] = useState({ connected: false, google_email: '' });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newEvent, setNewEvent] = useState({
    summary: '', description: '', start_date: '', start_time: '',
    end_date: '', end_time: '', location: '', attendees: ''
  });
  const [creating, setCreating] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await calendarApi.getStatus();
      setStatus(res.data);
      if (res.data.connected) {
        fetchEvents();
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  const fetchEvents = async () => {
    setEventsLoading(true);
    try {
      const res = await calendarApi.getEvents(30);
      setEvents(res.data);
    } catch (e) {
      if (e.response?.status === 400) {
        setStatus({ connected: false, google_email: '' });
      }
    }
    finally { setEventsLoading(false); }
  };

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Check URL for callback params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('calendar_connected') === 'true') {
      toast.success('Google Calendar connected!');
      fetchStatus();
      window.history.replaceState({}, '', '/google-calendar');
    }
    if (params.get('calendar_error')) {
      toast.error('Failed to connect Google Calendar');
      window.history.replaceState({}, '', '/google-calendar');
    }
  }, []);

  const handleConnect = async () => {
    try {
      const res = await calendarApi.getLoginUrl();
      window.location.href = res.data.authorization_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to initiate connection');
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Google Calendar? You can reconnect anytime.')) return;
    try {
      await calendarApi.disconnect();
      setStatus({ connected: false, google_email: '' });
      setEvents([]);
      toast.success('Google Calendar disconnected');
    } catch { toast.error('Failed to disconnect'); }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    if (!newEvent.summary || !newEvent.start_date || !newEvent.start_time || !newEvent.end_date || !newEvent.end_time) {
      toast.error('Please fill in all required fields');
      return;
    }
    setCreating(true);
    try {
      const start_time = `${newEvent.start_date}T${newEvent.start_time}:00Z`;
      const end_time = `${newEvent.end_date}T${newEvent.end_time}:00Z`;
      const attendees = newEvent.attendees ? newEvent.attendees.split(',').map(e => e.trim()).filter(Boolean) : [];

      const res = await calendarApi.createEvent({
        summary: newEvent.summary,
        description: newEvent.description,
        start_time, end_time,
        location: newEvent.location,
        attendees
      });
      toast.success('Event created!');
      setCreateDialogOpen(false);
      setNewEvent({ summary: '', description: '', start_date: '', start_time: '', end_date: '', end_time: '', location: '', attendees: '' });
      fetchEvents();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create event');
    } finally { setCreating(false); }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Delete this event from Google Calendar?')) return;
    try {
      await calendarApi.deleteEvent(eventId);
      setEvents(events.filter(e => e.id !== eventId));
      toast.success('Event deleted');
    } catch { toast.error('Failed to delete event'); }
  };

  const formatEventTime = (event) => {
    const start = event.start?.dateTime || event.start?.date;
    const end = event.end?.dateTime || event.end?.date;
    if (!start) return '';
    const s = new Date(start);
    const e = end ? new Date(end) : null;
    const dateOpts = { weekday: 'short', month: 'short', day: 'numeric' };
    const timeOpts = { hour: 'numeric', minute: '2-digit' };

    if (event.start?.date) {
      return s.toLocaleDateString('en-US', dateOpts) + ' (All day)';
    }
    const sameDay = e && s.toDateString() === e.toDateString();
    if (sameDay) {
      return `${s.toLocaleDateString('en-US', dateOpts)} · ${s.toLocaleTimeString('en-US', timeOpts)} – ${e.toLocaleTimeString('en-US', timeOpts)}`;
    }
    return `${s.toLocaleDateString('en-US', dateOpts)} ${s.toLocaleTimeString('en-US', timeOpts)} – ${e?.toLocaleDateString('en-US', dateOpts)} ${e?.toLocaleTimeString('en-US', timeOpts)}`;
  };

  const isUpcoming = (event) => {
    const start = event.start?.dateTime || event.start?.date;
    return start && new Date(start) > new Date();
  };

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><div className="w-10 h-10 loading-spinner" /></div>;

  return (
    <div className="space-y-6" data-testid="google-calendar-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <img src={EAGLE_MASCOT} alt="Calendar" className="w-14 h-14" />
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-[#1A2B5E] font-['Amaranth']">Google Calendar</h1>
          <p className="text-navy/60 mt-0.5">Sync your schedule with Google Calendar</p>
        </div>
      </div>

      {/* Connection status */}
      {!status.connected ? (
        <Card className="border-navy/10 border-dashed" data-testid="calendar-connect-card">
          <CardContent className="py-12 text-center">
            <CalendarDays className="w-16 h-16 mx-auto text-navy/20 mb-4" />
            <h2 className="text-xl font-bold text-[#1A2B5E] font-['Amaranth'] mb-2">Connect Google Calendar</h2>
            <p className="text-navy/60 max-w-md mx-auto mb-6">
              Link your Google Calendar to view upcoming events, create new ones, and stay on top of your schedule.
            </p>
            <Button
              onClick={handleConnect}
              className="bg-[#40CFDE] text-[#1A2B5E] hover:bg-[#40CFDE]/90 text-lg px-8 py-3"
              data-testid="connect-calendar-btn"
            >
              <Link2 className="w-5 h-5 mr-2" /> Connect Google Calendar
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Connected header */}
          <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-navy/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Check className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-[#1A2B5E]">Connected</p>
                <p className="text-sm text-navy/50">{status.google_email}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#40CFDE] text-[#1A2B5E] hover:bg-[#40CFDE]/90" data-testid="create-event-btn">
                    <Plus className="w-4 h-4 mr-2" /> New Event
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[500px]">
                  <DialogHeader>
                    <DialogTitle className="font-['Amaranth']">Create Calendar Event</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateEvent} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Event Title *</Label>
                      <Input
                        value={newEvent.summary}
                        onChange={e => setNewEvent({ ...newEvent, summary: e.target.value })}
                        placeholder="e.g., Client check-in meeting"
                        required
                        data-testid="event-title-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea
                        value={newEvent.description}
                        onChange={e => setNewEvent({ ...newEvent, description: e.target.value })}
                        placeholder="Meeting agenda or notes..."
                        rows={2}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Start Date *</Label>
                        <Input type="date" value={newEvent.start_date} onChange={e => setNewEvent({ ...newEvent, start_date: e.target.value, end_date: e.target.value || newEvent.end_date })} required data-testid="event-start-date" />
                      </div>
                      <div className="space-y-2">
                        <Label>Start Time *</Label>
                        <Input type="time" value={newEvent.start_time} onChange={e => setNewEvent({ ...newEvent, start_time: e.target.value })} required data-testid="event-start-time" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>End Date *</Label>
                        <Input type="date" value={newEvent.end_date} onChange={e => setNewEvent({ ...newEvent, end_date: e.target.value })} required data-testid="event-end-date" />
                      </div>
                      <div className="space-y-2">
                        <Label>End Time *</Label>
                        <Input type="time" value={newEvent.end_time} onChange={e => setNewEvent({ ...newEvent, end_time: e.target.value })} required data-testid="event-end-time" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Location</Label>
                      <Input
                        value={newEvent.location}
                        onChange={e => setNewEvent({ ...newEvent, location: e.target.value })}
                        placeholder="Office, Zoom link, etc."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Attendees (comma-separated emails)</Label>
                      <Input
                        value={newEvent.attendees}
                        onChange={e => setNewEvent({ ...newEvent, attendees: e.target.value })}
                        placeholder="john@example.com, jane@example.com"
                        data-testid="event-attendees"
                      />
                    </div>
                    <div className="flex justify-end gap-3 pt-2">
                      <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                      <Button type="submit" disabled={creating} className="bg-[#40CFDE] text-[#1A2B5E]" data-testid="submit-event-btn">
                        {creating ? 'Creating...' : 'Create Event'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
              <Button variant="outline" size="icon" onClick={fetchEvents} data-testid="refresh-events-btn">
                <RefreshCw className={`w-4 h-4 ${eventsLoading ? 'animate-spin' : ''}`} />
              </Button>
              <Button variant="outline" onClick={handleDisconnect} className="text-red-500 hover:bg-red-50" data-testid="disconnect-btn">
                <Unlink className="w-4 h-4 mr-1" /> Disconnect
              </Button>
            </div>
          </div>

          {/* Events list */}
          <div>
            <h2 className="text-xl font-bold text-[#1A2B5E] font-['Amaranth'] mb-4">Upcoming Events</h2>

            {eventsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 loading-spinner" />
              </div>
            ) : events.length === 0 ? (
              <Card className="border-navy/10 border-dashed">
                <CardContent className="py-12 text-center">
                  <CalendarDays className="w-12 h-12 mx-auto text-navy/20 mb-3" />
                  <p className="text-navy/50">No upcoming events</p>
                  <p className="text-sm text-navy/40 mt-1">Create a new event or add events in Google Calendar</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3" data-testid="events-list">
                {events.map(event => (
                  <Card key={event.id} className="border-navy/10 hover:shadow-md transition-shadow" data-testid={`event-${event.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-[#1A2B5E] truncate">{event.summary || 'Untitled'}</h3>
                            {isUpcoming(event) && <Badge className="bg-green-100 text-green-700 text-xs">Upcoming</Badge>}
                          </div>
                          <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-navy/60">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5" /> {formatEventTime(event)}
                            </span>
                            {event.location && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3.5 h-3.5" /> {event.location}
                              </span>
                            )}
                            {event.attendees?.length > 0 && (
                              <span className="flex items-center gap-1">
                                <Users className="w-3.5 h-3.5" /> {event.attendees.length} attendee{event.attendees.length > 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                          {event.description && (
                            <p className="text-sm text-navy/50 mt-2 line-clamp-2">{event.description}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {event.htmlLink && (
                            <a
                              href={event.htmlLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 rounded hover:bg-navy/5 text-navy/40 hover:text-[#106EFD] transition-colors"
                              title="Open in Google Calendar"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                          <button
                            onClick={() => handleDeleteEvent(event.id)}
                            className="p-2 rounded hover:bg-red-50 text-navy/30 hover:text-red-500 transition-colors"
                            data-testid={`delete-event-${event.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default GoogleCalendar;
